/**
 * One call in a case's answer key read against the account's tools: the definition it edits
 * against, the call written the way code calls a function, and the required inputs it still lacks.
 */

import { humanize } from '@/modules/simulator/ui/text';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import type { DatasetExpectedTool } from '@/types/simulation-dataset.types';
import { requiredOf, schemaFields } from './tool-schema';

const asRecord = (value: unknown): Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/** What a tool takes and whether it writes, without the wording around it. */
const shapeKey = (definition: ToolDefinitionAction): string => {
	const schema = asRecord(definition.inputSchema);
	const properties = asRecord(schema['properties']);
	return JSON.stringify([
		Object.keys(properties)
			.sort()
			.map((name) => [name, asRecord(properties[name])['type'] ?? null]),
		[...requiredOf(schema)].sort(),
		definition.riskLevel
	]);
};

interface ResolvedCall {
	/**
	 * The definition the call edits against; null when the account has no such tool, or when several
	 * integrations have it with different inputs and the call does not say which ran it.
	 */
	definition: ToolDefinitionAction | null;
	/** Those integrations, when that is why there is no definition. */
	sharedBy: string[];
}

/**
 * The definition a call edits against: its integration's own, or the only one by its name. A call
 * with no integration whose name several integrations share runs on the first of them the account
 * has connected, in an order the tool list does not show. When they all take the same inputs that
 * choice changes nothing; when they differ, the call's own arguments are all that is known.
 */
export const resolveCall = (
	call: Pick<DatasetExpectedTool, 'name' | 'provider'>,
	definitions: ToolDefinitionAction[]
): ResolvedCall => {
	if (call.provider) {
		const own = definitions.find((entry) => entry.provider === call.provider && entry.tool === call.name);
		return { definition: own ?? null, sharedBy: [] };
	}
	const named = definitions.filter((entry) => entry.tool === call.name);
	const [first] = named;
	if (!first) return { definition: null, sharedBy: [] };
	if (named.every((entry) => shapeKey(entry) === shapeKey(first))) {
		return { definition: named.length > 1 ? { ...first, provider: '' } : first, sharedBy: [] };
	}
	return { definition: null, sharedBy: [...new Set(named.map((entry) => entry.provider))].sort() };
};

/**
 * How a call reads: its tool's label and the integration it runs on. A call with no integration still
 * belongs to one when only one integration has its tool.
 */
export const callIdentity = (
	call: Pick<DatasetExpectedTool, 'name' | 'provider'>,
	definitions: ToolDefinitionAction[]
): { name: string; provider: string | null } => {
	const { definition } = resolveCall(call, definitions);
	return { name: definition?.label ?? humanize(call.name), provider: call.provider ?? (definition?.provider || null) };
};

/** The same tool, whichever integration a call that names none ran it on. */
export const sameTool = (a: Pick<DatasetExpectedTool, 'name' | 'provider'>, b: Pick<DatasetExpectedTool, 'name' | 'provider'>): boolean =>
	a.name === b.name && (a.provider === null || b.provider === null || a.provider === b.provider);

/** The calls with this one in place of the call at `index`, or added after them when `index` is null. */
export const putCall = (tools: DatasetExpectedTool[], call: DatasetExpectedTool, index: number | null): DatasetExpectedTool[] =>
	index === null ? [...tools, call] : tools.map((entry, i) => (i === index ? call : entry));

const LONG_VALUE = 40;

const valueText = (value: unknown): string => {
	if (typeof value === 'number' || typeof value === 'boolean' || value === null) return String(value);
	const text = JSON.stringify(value) ?? String(value);
	return text.length > LONG_VALUE ? `${text.slice(0, LONG_VALUE - 1)}…` : text;
};

/** `name(input: value, ...)`, strings quoted, long values cut short. */
export const callText = (call: Pick<DatasetExpectedTool, 'name' | 'args'>): string =>
	`${call.name}(${Object.entries(call.args)
		.map(([name, value]) => `${name}: ${valueText(value)}`)
		.join(', ')})`;

const isSet = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

/** The inputs the tool requires that the call has not set. */
export const missingInputs = (call: Pick<DatasetExpectedTool, 'args'>, definition: ToolDefinitionAction | null): string[] =>
	definition
		? schemaFields(definition.inputSchema)
				.filter((field) => field.required && !isSet(call.args[field.name]))
				.map((field) => field.name)
		: [];
